"use client";

import { Difficulty } from "shared/games/Difficulty";
import { Game } from "shared/games/types/Game";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import questionFromTodo from "shared/games/questions/questionFromTodo";
import { GameQuestion } from "shared/games/types/GameQuestions";
import useTodosContext from "shared/todos/useTodosContext";
import { Todo } from "shared/todos/Todo";
import { selectCompletedTodos } from "shared/todos/useTodosReducer";

interface Shared {
  roundId: number;
}

interface TodosByCategory extends Shared {
  type: "by-category";
  difficulty: Difficulty;
  games: Game[];
}

interface TodosByID extends Shared {
  type: "by-id";
  todoIds: number[];
}

export type Props = TodosByCategory | TodosByID;

interface Return {
  currentQuestion: GameQuestion | null;
  next: () => void;
  remaining: number;
}

/**
 * Use a queue to store the remaining todos when it gets to the end check if there are any more and start over or end the round
 *
 * Support two modes:
 *  - by-category: get all todos for the given category and difficulty. Keep track of completed ids and start over when the queue is empty
 *  - by-id: get the todos for the given todo_ids in order. Only go through the list once regardless of completion
 */
export function useTodoQuestions(props: Props): Return {
  const { roundId } = props;
  const [state] = useTodosContext();
  const [todoQueue, setTodoQueue] = useState<{
    queue: Todo[] | null;
    cacheBuster: number;
  }>({ queue: null, cacheBuster: Math.random() });
  const once = useRef(false);
  const queue = useMemo(() => {
    if (props.type === "by-category") {
      const completedTodos = selectCompletedTodos(state);
      const { games, difficulty } = props;
      const queue = state.todos.filter(
        (todo) =>
          games.includes(todo.game) &&
          todo.difficulty === difficulty &&
          !completedTodos.has(todo.id)
      );

      return queue;
    }

    const todosById = state.todos.reduce(
      (acc, todo) => ({
        ...acc,
        [todo.id]: todo,
      }),
      {} as Record<number, Todo>
    );

    return props.todoIds.map((id) => todosById[id]);
  }, [props, state]);

  /**
   * Set the queue once after the todos are finished loading
   */
  useEffect(() => {
    if (state.state !== "loaded") {
      return;
    }

    if (once.current) {
      return;
    }

    once.current = true;

    const set = () => setTodoQueue((prev) => ({ ...prev, queue }));
    const loadingDuration = 2000;

    setTimeout(set, loadingDuration);
  }, [queue, state]);

  /**
   * Current question
   */
  const currentQuestion = useMemo(() => {
    if (!todoQueue.queue) {
      return null;
    }

    const [nextTodo] = todoQueue.queue;

    if (!nextTodo) {
      return null;
    }

    return questionFromTodo({
      roundId,
      todo: nextTodo,
    });
  }, [roundId, todoQueue]);

  /**
   * Next question
   */
  const next = useCallback(() => {
    setTodoQueue((prev) => {
      if (!prev.queue) {
        return prev;
      }

      let nextQueue = prev.queue.slice(1);

      // if the queue is empty, check if we need to start over
      if (nextQueue.length === 0) {
        // IF in category mode AND there are no more todos, then start the queue over
        // In id mode we only go through the passed in list once regardless of completion
        if (props.type === "by-category") {
          if (queue.length > 0) {
            nextQueue = queue;
          }
        }
      }

      return { ...prev, queue: nextQueue, cacheBuster: Math.random() };
    });
  }, [props.type, queue]);

  if (todoQueue.queue === null) {
    return {
      currentQuestion: null,
      next: () => {},
      remaining: Infinity,
    };
  }

  return {
    currentQuestion,
    next,
    remaining: todoQueue.queue?.length ?? 0,
  } as const;
}
